import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Timer } from "@/components/Timer";
import { SoundSelector } from "@/components/SoundSelector";
import { SoundAttribution } from "@/components/SoundAttribution";
import { 
  Play, Pause, RefreshCw, Maximize2, Minimize2,
  Coffee, ClipboardList, RotateCcw, Settings
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { playCompletionSound } from '@/utils/audioUtils';

type TimerMode = 'focus' | 'shortBreak' | 'longBreak';

interface PomodoroSettings {
  focusDuration: number;
  shortBreakDuration: number;
  longBreakDuration: number;
  longBreakInterval: number;
}

interface StreakData {
  daily: number;
  weekly: number;
  lastCompleted: string | null;
}

const defaultSettings: PomodoroSettings = {
  focusDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  longBreakInterval: 4
};

const getMotivationalMessage = (streak: number): string => {
  const messages = [
    "Great start! Keep going!",
    "You're building momentum!",
    "Fantastic focus! Keep it up!",
    "You're on fire today!",
    "Incredible discipline! You're unstoppable!",
    "Amazing focus! You're crushing it!",
    "Deep work mastery achieved!"
  ];
  
  const index = Math.min(Math.floor(streak / 2), messages.length - 1);
  return messages[index];
};

export const PomodoroTimer = () => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [mode, setMode] = useState<TimerMode>('focus');
  const [settings, setSettings] = useState<PomodoroSettings>(() => {
    const savedSettings = localStorage.getItem('pomodoro-settings');
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
  });
  const [selectedSound, setSelectedSound] = useState<string>(
    localStorage.getItem('pomodoro-sound') || 'https://assets.mixkit.co/active_storage/sfx/2434/2434-preview.mp3'
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sessionLabel, setSessionLabel] = useState<string>('Focus Session');
  const [currentPomodoro, setCurrentPomodoro] = useState(1);
  const [streak, setStreak] = useState<StreakData>(() => {
    const savedStreak = localStorage.getItem('pomodoro-streak');
    return savedStreak ? JSON.parse(savedStreak) : {
      daily: 0,
      weekly: 0,
      lastCompleted: null
    };
  });
  const [showMotivationalMessage, setShowMotivationalMessage] = useState(false);
  const [motivationalMessage, setMotivationalMessage] = useState('');

  const currentDuration = useMemo(() => {
    switch (mode) {
      case 'focus':
        return settings.focusDuration;
      case 'shortBreak':
        return settings.shortBreakDuration;
      case 'longBreak':
        return settings.longBreakDuration;
      default:
        return settings.focusDuration;
    }
  }, [mode, settings]);

  const resetTimer = useCallback(() => {
    setIsActive(false);
    setIsPaused(false);
    setMode('focus');
    setCurrentPomodoro(1);
    setSessionLabel('Focus Session');
  }, []);

  const saveSettings = useCallback((newSettings: PomodoroSettings) => {
    setSettings(newSettings);
    localStorage.setItem('pomodoro-settings', JSON.stringify(newSettings));
    setIsSettingsOpen(false);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (!isActive) {
      setIsActive(true);
      setIsPaused(false);
    } else {
      setIsPaused(!isPaused);
    }
  }, [isActive, isPaused]);

  useEffect(() => {
    localStorage.setItem('pomodoro-sound', selectedSound);
  }, [selectedSound]);

  useEffect(() => {
    localStorage.setItem('pomodoro-streak', JSON.stringify(streak));
  }, [streak]);

  const handleComplete = useCallback(() => {
    playCompletionSound();
    
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => console.error("Error playing notification:", err));
    } catch (error) {
      console.error("Error playing sound:", error);
    }

    if (mode === 'focus') {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      
      setStreak(prev => {
        const isNewDay = prev.lastCompleted !== today;
        
        return {
          daily: isNewDay ? 1 : prev.daily + 1,
          weekly: prev.weekly + 1,
          lastCompleted: today
        };
      });
      
      setMotivationalMessage(getMotivationalMessage(streak.daily));
      setShowMotivationalMessage(true);
      setTimeout(() => setShowMotivationalMessage(false), 3000);
    }

    if (mode === 'focus') {
      if (currentPomodoro % settings.longBreakInterval === 0) {
        setMode('longBreak');
        setSessionLabel('Long Break');
      } else {
        setMode('shortBreak');
        setSessionLabel('Short Break');
      }
    } else {
      setMode('focus');
      setSessionLabel('Focus Session');
      if (mode === 'shortBreak' || mode === 'longBreak') {
        setCurrentPomodoro(prev => prev + 1);
      }
    }
    
    setIsActive(true);
    setIsPaused(false);
  }, [mode, currentPomodoro, settings, streak.daily]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className="flex flex-col items-center space-y-6 w-full max-w-xl mx-auto">
      <div className="flex items-center justify-between w-full mb-4">
        <div className="flex gap-2">
          <Badge variant={mode === 'focus' ? "default" : "outline"} className="cursor-pointer" onClick={() => !isActive && setMode('focus')}>
            Focus
          </Badge>
          <Badge variant={mode === 'shortBreak' ? "default" : "outline"} className="cursor-pointer" onClick={() => !isActive && setMode('shortBreak')}>
            Short Break
          </Badge>
          <Badge variant={mode === 'longBreak' ? "default" : "outline"} className="cursor-pointer" onClick={() => !isActive && setMode('longBreak')}>
            Long Break
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setIsSettingsOpen(true)} title="Settings">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="w-full max-w-xs">
        <Input
          value={sessionLabel}
          onChange={(e) => setSessionLabel(e.target.value)}
          className="text-center font-medium"
          placeholder="What are you working on?"
          disabled={isActive && !isPaused}
        />
      </div>
      
      <div className="relative flex flex-col items-center space-y-6">
        <div className="scale-125 transform">
          <Timer
            duration={currentDuration}
            onComplete={handleComplete}
            isActive={isActive && !isPaused}
            isPaused={isPaused}
            onPlayPause={togglePlayPause}
            onReset={resetTimer}
          />
        </div>
        
        <div className="flex gap-4">
          <div className="text-center">
            <span className="text-xs text-muted-foreground">Today</span>
            <p className="font-bold text-sm">{streak.daily}</p>
          </div>
          <div className="text-center">
            <span className="text-xs text-muted-foreground">Week</span>
            <p className="font-bold text-sm">{streak.weekly}</p>
          </div>
          <div className="text-center">
            <span className="text-xs text-muted-foreground">Pomodoro</span>
            <p className="font-bold text-sm">{currentPomodoro}</p>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-center gap-4">
        <Button 
          variant="outline" 
          size="icon" 
          onClick={togglePlayPause}
          className="h-12 w-12 rounded-full"
        >
          {isActive && !isPaused ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={resetTimer}
          className="h-10 w-10 rounded-full"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="flex items-center gap-2">
        {mode === 'focus' ? (
          <ClipboardList className="h-4 w-4 text-primary" />
        ) : (
          <Coffee className="h-4 w-4 text-primary" />
        )}
        <span className="text-sm font-medium">
          {mode === 'focus' ? 'Focus Time' : mode === 'shortBreak' ? 'Short Break' : 'Long Break'}
        </span>
      </div>

      <div>
        <SoundSelector 
          onSoundSelect={setSelectedSound} 
          selectedSound={selectedSound} 
        />
      </div>
      
      <SoundAttribution />
      
      {showMotivationalMessage && (
        <div className="py-2 px-4 bg-primary/10 text-primary rounded-md animate-fade-in">
          {motivationalMessage}
        </div>
      )}
      
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pomodoro Settings</DialogTitle>
            <DialogDescription>
              Customize your pomodoro timer durations.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="focusDuration">Focus duration (minutes)</Label>
              <Input
                id="focusDuration"
                type="number"
                min="1"
                max="60"
                defaultValue={settings.focusDuration}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 60) {
                    setSettings({...settings, focusDuration: value});
                  }
                }}
              />
            </div>
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="shortBreakDuration">Short break (minutes)</Label>
              <Input
                id="shortBreakDuration"
                type="number"
                min="1"
                max="30"
                defaultValue={settings.shortBreakDuration}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 30) {
                    setSettings({...settings, shortBreakDuration: value});
                  }
                }}
              />
            </div>
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="longBreakDuration">Long break (minutes)</Label>
              <Input
                id="longBreakDuration"
                type="number"
                min="1"
                max="60"
                defaultValue={settings.longBreakDuration}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 60) {
                    setSettings({...settings, longBreakDuration: value});
                  }
                }}
              />
            </div>
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="longBreakInterval">Pomodoros until long break</Label>
              <Input
                id="longBreakInterval"
                type="number"
                min="1"
                max="10"
                defaultValue={settings.longBreakInterval}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 10) {
                    setSettings({...settings, longBreakInterval: value});
                  }
                }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettings(defaultSettings)}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset to Defaults
            </Button>
            <DialogClose asChild>
              <Button onClick={() => saveSettings(settings)}>Save Changes</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
